/* eslint-disable no-return-assign */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { BehaviorSubject, Subject } from "rxjs";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { EventStream, xstateDevTools } from "@iventis/utilities";
import { styled } from "@iventis/styles";
import { getTestAssetUrl, getTestModel, TestingAssetId, TestingModelId } from "./map-testing.helpers";
import { MapState, StoredBounds, StoredPosition } from "../types/store-schema";
import { MapboxEngine } from "../bridge/mapbox/engine-mapbox";
import { EventMessageMap } from "../types/map-stream.types";
import { MAPBOX_MAX_ZOOM, MAPBOX_MIN_ZOOM } from "../bridge/constants";
import { MapStoreEventType } from "../types/events-store";
import { startingTestPosition, modelLayerId, origin, cameraPositions, initialState, modelValues, ModelPerformanceData } from "./model-testing-constants";
import { createLocalObjects, calculateModelPlacement, downloadResults } from "./model-testing-helpers";
import { mapFontStackUrl } from "../map-fonts";

const mapboxKey = "pk.eyJ1Ijoiam9lY3VzZGluIiwiYSI6IjFmMzMzODhmYTQ4ZWM4MmYwZGVlZDMwMTJmMDIyZWQwIn0.8taIUk7YHSD1srUfcacXBA";

export const ModelTestingComponent: React.FunctionComponent = () => {
    // Map and store setup
    const storeRef = useRef<BehaviorSubject<MapState>>();
    const eventStreamRef = useRef<EventStream<EventMessageMap>>(null);
    const mapElementRef = useRef<HTMLDivElement>();
    const mapEngine = useRef<MapboxEngine>();
    const mapMovement$ = useRef(new Subject<void>());

    const [isTestingRunning, setIsTestingRunning] = useState(false);

    // Performance data
    const [performanceData, setPerformanceData] = useState<ModelPerformanceData[]>([]);

    // Test data
    const [amountOfModels, setAmountOfModel] = useState<number>(modelValues[0]);
    const cameraPositionCount = useRef<number>(0);
    const fpsValues = useRef<number[]>([]);

    const modifyStore = (change: Partial<MapState>) => {
        storeRef.current.next({
            ...storeRef.current.value,
            ...change,
        });
    };

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (mapElementRef.current !== undefined) {
            storeRef.current = new BehaviorSubject(initialState);
            eventStreamRef.current = new EventStream<EventMessageMap>();
            const engine = new MapboxEngine({
                store: { change: storeRef.current },
                container: mapElementRef.current,
                eventStream: eventStreamRef.current,
                mbxAccessToken: mapboxKey,
                preview: false,
                modifierKeys: ["Control", "Meta"],
                assetOptions: {
                    assetUrlGetter: async (id: TestingAssetId) => getTestAssetUrl(id),
                    multipleAssetUrlGetter: async (ids: TestingAssetId[]) => ids.map((id) => ({ id, url: getTestAssetUrl(id) })),
                    multipleModelsGetter: async (ids: TestingModelId[]) => ids.map((id) => getTestModel(id)),
                    isAssetSdf: async () => false,
                    bustCacheIds: () => null,
                },
                maxZoom: MAPBOX_MAX_ZOOM,
                minZoom: MAPBOX_MIN_ZOOM,
                fontStackUrl: mapFontStackUrl,
                devTools: xstateDevTools,
                getAttributeListItems: () => Promise.resolve([]),
                routeApiFunctions: undefined,
                isExternalUser: false,
                user: { id: "test", isMobileUser: false },
                setWebGLSupported: null,
            });

            mapEngine.current = engine;

            const storeSubscription = engine.storeEvents.subscribe((event) => {
                switch (event.eventType) {
                    case MapStoreEventType.SET_POSITION:
                        mapMovement$.current.next();
                        break;
                    default:
                }
            });
            return () => {
                engine.destroy();
                storeSubscription.unsubscribe();
                storeRef.current.unsubscribe();
                eventStreamRef.current.destroy();
            };
        }
    }, [mapElementRef.current]);

    const setStartPosition = (position: StoredPosition) => {
        modifyStore({
            position: {
                value: position,
                stamp: uuid(),
            },
        });
    };

    const setCameraPosition = (bounds: StoredBounds) => {
        modifyStore({
            bounds: {
                value: bounds,
                stamp: uuid(),
            },
        });
    };

    /** Called when the test is started */
    const handleStartTest = () => {
        setStartPosition(startingTestPosition);
        setIsTestingRunning(true);

        const times = [];
        let fps;
        let testing = false;
        fpsValues.current = [];

        /** Measures the FPS of the viewport */
        const refreshLoop = () => {
            window.requestAnimationFrame(() => {
                const now = performance.now();
                while (times.length > 0 && times[0] <= now - 1000) {
                    times.shift();
                }
                times.push(now);
                fps = times.length;
                if (testing) {
                    fpsValues.current.push(fps);
                }
                refreshLoop();
            });
        };

        refreshLoop();
        testing = true;

        /** Handles getting the next camera position once the map movement has finished */
        const sub = mapMovement$.current.subscribe(() => {
            const position = cameraPositions[cameraPositionCount.current];
            if (position == null) {
                sub.unsubscribe();
                testing = false;
                setData();
                setIsTestingRunning(false);
            } else {
                cameraPositionCount.current += 1;
                setCameraPosition(position);
            }
        });
    };

    /** Handles calculating the average FPS and other values */
    const setData = () => {
        const averageFPS = fpsValues.current.reduce((sum, val) => (sum += val), 0) / fpsValues.current.length;
        setPerformanceData([
            ...performanceData,
            {
                id: uuid(),
                testNumber: performanceData.length + 1,
                amountOfModels,
                averageFPS: averageFPS.toFixed(2),
                below30FPS: ((fpsValues.current.filter((val) => val < 30).length / fpsValues.current.length) * 100).toFixed(2),
                below20FPS: ((fpsValues.current.filter((val) => val < 20).length / fpsValues.current.length) * 100).toFixed(2),
                below10FPS: ((fpsValues.current.filter((val) => val < 10).length / fpsValues.current.length) * 100).toFixed(2),
            },
        ]);
        fpsValues.current = [];
        cameraPositionCount.current = 0;
    };

    /** When the amount of models is changed */
    const handleModelsChanged = (stringAmount: string) => {
        const amount = Number(stringAmount);
        setAmountOfModel(amount);
    };

    // Use separate useEffect to add models to store
    useEffect(() => {
        const localObjects = createLocalObjects(modelLayerId, calculateModelPlacement(amountOfModels, origin), 0);
        modifyStore({
            geoJSON: {
                value: {
                    [modelLayerId]: localObjects,
                },
                stamp: uuid(),
            },
        });
    }, [amountOfModels]);

    return (
        <ParentContainer>
            <div ref={mapElementRef} style={{ height: "600px", width: "900px", border: "1px solid black", flexShrink: 0 }} />
            <div style={{ flexShrink: 0 }}>
                Amount of models
                <select value={amountOfModels} disabled={isTestingRunning} onChange={(event) => handleModelsChanged(event.target.value)}>
                    {modelValues.map((value) => (
                        <option key={value} value={value}>
                            {value}
                        </option>
                    ))}
                </select>
                <button type="button" onClick={() => downloadResults(performanceData)}>
                    Download Results
                </button>
                <button type="button" disabled={isTestingRunning} onClick={handleStartTest}>
                    Start Test
                </button>
            </div>
            <PerformanceDataTable>
                <tbody style={{ display: "table", width: "100%" }}>
                    <tr>
                        <th>Test Number</th>
                        <th>Amount of Models</th>
                        <th>Average FPS</th>
                        <th>Below 30 FPS (%)</th>
                        <th>Below 20 FPS (%)</th>
                        <th>Below 10 FPS (%)</th>
                    </tr>
                    {performanceData.map((value) => (
                        <tr key={value.id}>
                            <td>{value.testNumber}</td>
                            <td>{value.amountOfModels}</td>
                            <td>{value.averageFPS}</td>
                            <td>{value.below30FPS}</td>
                            <td>{value.below20FPS}</td>
                            <td>{value.below10FPS}</td>
                        </tr>
                    ))}
                </tbody>
            </PerformanceDataTable>
        </ParentContainer>
    );
};

const ParentContainer = styled.div`
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PerformanceDataTable = styled.table`
    display: block;
    overflow: auto;
    text-align: center;
    tr:nth-child(even) {
        background-color: #e5e5e5;
    }
`;

export default ModelTestingComponent;
